import * as React from "react";
import { useState, forwardRef } from "react";
import { Card, CardContent, CardHeader, makeStyles, TextField } from "@material-ui/core";
import Button from "./ui/button";

import { useAuth } from "../hooks/use-auth";
import { useAdminContext } from "../hooks/use-admin-context";

import { userLogin, userResetPassword, userUpdatePassword } from "../services/api";

const LOGIN = 0;
const RESET = 1;

const useStyles = makeStyles(() => {
  return {
    root: {
      width: "100vw",
      height: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    form: {
      width: "100%",
      maxWidth: 400
    },
    formHeader: {
      backgroundColor: "#f5f5f5",
      textAlign: "center"
    },
    formInner: {
      padding: 25
    },
    formControl: {
      margin: "10px 0"
    },
    formButton: {
      marginTop: 10
    },
    formModal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    formModalHeader: {
      padding: "25px 25px 0 25px"
    },
    formModalInner: {
      padding: "0 25px 25px 25px"
    }
  };
});

const ResetModalContent = forwardRef((_, ref) => {
  const classes = useStyles();
  const { setModal, setSnackbar } = useAdminContext();
  const [email, setEmail] = useState();

  const onInputChange = (e) => {
    const { value } = e.target;
    setEmail(value);
  };

  const submitForm = async () => {
    const { status } = await userResetPassword(email);
    if (status === "reset_password") {
      setModal({ open: false });
      setSnackbar({
        message: "Mot de passe réinitialisé, veuillez consulter vos emails",
        open: true,
        severity: "success"
      });
    } else {
      setSnackbar({
        message: "Une erreur est survenue, veuillez réessayer",
        open: true,
        severity: "error"
      });
    }
  };

  return (
    <Card ref={ref}>
      <CardHeader className={classes.formModalHeader} title="Réinitialiser mon mot de passe" />
      <CardContent className={classes.formModalInner}>
        <TextField
          className={classes.formControl}
          label="Email"
          id="email"
          name="email"
          type="email"
          onChange={onInputChange}
          required
          fullWidth
        />
        <Button
          className={classes.formButton}
          disabled={!email || email.trim() === ""}
          color="primary"
          variant="contained"
          type="submit"
          onClick={() => submitForm()}
          fullWidth
        >
          Valider
        </Button>
      </CardContent>
    </Card>
  );
});

const LoginForm = ({ data, setData, submitForm }) => {
  const classes = useStyles();
  const { setModal } = useAdminContext();

  const onInputChange = (e) => {
    const { value, name } = e.target;
    setData((data) => {
      return { ...data, [name]: value };
    });
  };

  const isFormValid = () => {
    if (!data.email || !data.password) return false;
    if (data.email.trim() === "" || data.password.trim() === "") return false;
    return true;
  };

  const onResetClick = () => {
    setModal({
      open: true,
      children: <ResetModalContent />,
      className: classes.formModal
    });
  };

  return (
    <>
      <TextField
        className={classes.formControl}
        label="Email"
        id="email"
        name="email"
        type="email"
        onChange={onInputChange}
        required
        fullWidth
      />
      <TextField
        className={classes.formControl}
        label="Mot de passe"
        id="password"
        name="password"
        type="password"
        onChange={onInputChange}
        required
        fullWidth
      />
      <Button
        className={classes.formButton}
        disabled={!isFormValid()}
        color="primary"
        variant="contained"
        type="submit"
        onClick={() => submitForm(false)}
        fullWidth
      >
        Connexion
      </Button>
      <Button className={classes.formButton} onClick={onResetClick}>
        Mot de passe oublié ?
      </Button>
    </>
  );
};

const ResetForm = ({ setData, submitForm }) => {
  const classes = useStyles();
  const [resetData, setResetData] = useState({});

  const onInputChange = (e) => {
    const { value, name } = e.target;
    setResetData((data) => ({ ...data, [name]: value }));
    setData((data) => ({ ...data, password: value }));
  };

  const isFormValid = () => {
    if (!resetData.password || !resetData.confirmPassword) return false;
    if (resetData.password.trim() === "" || resetData.confirmPassword.trim() === "") return false;
    if (resetData.password !== resetData.confirmPassword) return false;
    return true;
  };

  return (
    <>
      <TextField
        className={classes.formControl}
        label="Nouveau mot de passe"
        id="password"
        name="password"
        type="password"
        onChange={onInputChange}
        required
        fullWidth
      />
      <TextField
        className={classes.formControl}
        label="Confirmer le nouveau mot de passe"
        id="confirmPassword"
        name="confirmPassword"
        type="password"
        onChange={onInputChange}
        required
        fullWidth
      />
      <Button
        className={classes.formButton}
        disabled={!isFormValid()}
        color="primary"
        variant="contained"
        type="submit"
        onClick={() => submitForm(true)}
        fullWidth
      >
        Valider
      </Button>
    </>
  );
};

const Login = () => {
  const classes = useStyles();
  const { setSnackbar } = useAdminContext();
  const { login } = useAuth();
  const [data, setData] = useState({});
  const [mode, setMode] = useState(LOGIN);
  const [resetToken, setResetToken] = useState();

  const Form = mode === LOGIN ? LoginForm : ResetForm;

  const submitForm = async (update) => {
    const { email, password } = data;
    try {
      const endpoint = update ? userUpdatePassword : userLogin;
      const { token, status } = await endpoint(email, password, resetToken);
      if (status && status === "reset_password") {
        setMode(RESET);
        setResetToken(token);
      } else if (token) {
        login(token);
      }
    } catch (error) {
      const { code = "", message = "Une erreur est survenue, veuillez réessayer" } = error;
      setSnackbar({
        message: `${code} ${message}`,
        open: true,
        severity: "error"
      });
    }
  };

  return (
    <div className={classes.root}>
      <Card className={classes.form}>
        <CardHeader className={classes.formHeader} title="[Admin] Salmo-check" />
        <CardContent className={classes.formInner}>
          <Form data={data} setData={setData} submitForm={submitForm} />
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
